// Fix-once speakers: the client model (BUILD 1 WP-6). Pure functions shared by
// the speaker panel, the row popover and the transcript; no React here, so
// test/workspace-meeting-speakers.mjs runs them directly.
//
// Resolution mirrors the server (src/company-workspace/meetings/speaker-model.mjs):
// the row's own person; else the voice's person, following mergedInto; else
// "not a participant"; else Speaker N. Names come from the meeting's
// participants; the UI renders `speakerN{n}`, never the English string.
// Back-channels are found by a structural count: a row of 1–3 words that
// starts inside (or within 1 s of) the running row of another speaker.
import type {
  CompanyMeeting,
  MeetingCaptureGap,
  MeetingParticipant,
  MeetingSegment,
  MeetingSpeaker,
} from './types';

export type SpeakerKind = MeetingSpeaker['kind'];

/** Who said a row, as the UI shows it. */
export interface RowSpeaker {
  /** The voice after merges; null when the engine gave the row no label. */
  label: string | null;
  speakerNumber: number | null;
  kind: SpeakerKind;
  participantId: string | null;
  name: string | null;
  confirmed: boolean;
  /** The row's own person (a row-scope choice), not the voice's. */
  rowOverride: boolean;
}

export interface SpeakerIndex {
  voices: Map<string, MeetingSpeaker>;
  participants: Map<string, MeetingParticipant>;
  numbers: Map<string, number>;
}

const byTranscriptOrder = (left: MeetingSegment, right: MeetingSegment) =>
  (left.ordinal ?? left.index) - (right.ordinal ?? right.index) || left.index - right.index;

/** N by first appearance of each engine label: the server's rule, for meetings read before it numbered them. */
function firstAppearance(segments: MeetingSegment[]) {
  const numbers = new Map<string, number>();
  for (const segment of [...segments].sort(byTranscriptOrder)) {
    const label = segment.rawSpeaker ?? segment.speaker;
    if (label != null && !numbers.has(label)) numbers.set(label, numbers.size + 1);
  }
  return numbers;
}

export function speakerIndex(meeting: CompanyMeeting): SpeakerIndex {
  const voices = new Map((meeting.speakers ?? []).map((voice) => [voice.label, voice]));
  const numbers = firstAppearance(meeting.segments ?? []);
  for (const voice of voices.values())
    if (voice.speakerNumber != null) numbers.set(voice.label, voice.speakerNumber);
  return {
    voices,
    participants: new Map((meeting.participants ?? []).map((person) => [person.id, person])),
    numbers,
  };
}

/** Follow mergedInto to the voice that owns the rows (cycle-safe). */
export function rootVoice(voices: Map<string, MeetingSpeaker>, label: string | null) {
  let voice = label == null ? null : (voices.get(label) ?? null);
  const seen = new Set<string>();
  while (voice?.mergedInto && !seen.has(voice.label)) {
    seen.add(voice.label);
    const next = voices.get(voice.mergedInto);
    if (!next) break;
    voice = next;
  }
  return voice;
}

const personName = (index: SpeakerIndex, participantId: string, fallback: string | null) =>
  index.participants.get(participantId)?.displayName ?? fallback;

function voiceIdentity(voice: MeetingSpeaker | null, index: SpeakerIndex) {
  if (voice?.kind === 'person' && voice.participantId)
    return {
      kind: 'person' as const,
      participantId: voice.participantId,
      name: personName(index, voice.participantId, voice.name),
      confirmed: true,
    };
  if (voice?.kind === 'not_participant')
    return { kind: 'not_participant' as const, participantId: null, name: null, confirmed: true };
  return { kind: 'unknown' as const, participantId: null, name: null, confirmed: false };
}

export function resolveRowSpeaker(segment: MeetingSegment, index: SpeakerIndex): RowSpeaker {
  const voice = rootVoice(index.voices, segment.speaker);
  const label = voice?.label ?? segment.speaker;
  const base = { label, speakerNumber: label == null ? null : (index.numbers.get(label) ?? null) };
  if (segment.participantId)
    return {
      ...base,
      kind: 'person',
      participantId: segment.participantId,
      name: personName(index, segment.participantId, null),
      confirmed: true,
      rowOverride: true,
    };
  return { ...base, ...voiceIdentity(voice, index), rowOverride: false };
}

/** A voice's own identity, for its chip (a merged voice shows where it went elsewhere). */
export const voiceSpeaker = (voice: MeetingSpeaker, index: SpeakerIndex): RowSpeaker => ({
  label: voice.label,
  speakerNumber: index.numbers.get(voice.label) ?? voice.speakerNumber ?? null,
  ...voiceIdentity(voice, index),
  rowOverride: false,
});

/** Words in a row: engine words when present, else the transcript text available to this reader. */
export function wordCount(segment: MeetingSegment): number {
  if (segment.words) return segment.words.length;
  // Read-only colleagues receive the display text, never the editor's raw transcript.
  return (segment.rawText ?? segment.displayText).split(' ').filter(Boolean).length;
}

export const BACKCHANNEL_MAX_WORDS = 3;
export const BACKCHANNEL_SLACK_MS = 1000;

const who = (speaker: RowSpeaker) =>
  speaker.participantId
    ? `person:${speaker.participantId}`
    : speaker.label
      ? `voice:${speaker.label}`
      : null;

/** A short row from another speaker that starts inside the running row (or within 1 s of its end). */
export function isBackchannel(
  row: { segment: MeetingSegment; speaker: RowSpeaker },
  running: { segment: MeetingSegment; speaker: RowSpeaker } | null,
): boolean {
  if (!running) return false;
  const words = wordCount(row.segment);
  if (words < 1 || words > BACKCHANNEL_MAX_WORDS) return false;
  const { startMs } = row.segment;
  const { startMs: runningStart, endMs: runningEnd } = running.segment;
  if (startMs == null || runningStart == null || runningEnd == null) return false;
  const speaker = who(row.speaker);
  if (!speaker || speaker === who(running.speaker)) return false;
  return startMs >= runningStart && startMs <= runningEnd + BACKCHANNEL_SLACK_MS;
}

export type TranscriptRow = {
  kind: 'row';
  segment: MeetingSegment;
  speaker: RowSpeaker;
  reactions: { segment: MeetingSegment; speaker: RowSpeaker }[];
};
export type TranscriptGap = { kind: 'gap'; gap: MeetingCaptureGap };
export type TranscriptItem = TranscriptRow | TranscriptGap;

function takeGaps(gaps: MeetingCaptureGap[], beforeMs: number | null, items: TranscriptItem[]) {
  while (gaps.length && beforeMs != null && gaps[0].atMs <= beforeMs)
    items.push({ kind: 'gap', gap: gaps.shift() as MeetingCaptureGap });
}

/**
 * The transcript as the UI lays it out: rows in transcript order, each short
 * reply from another speaker folded into the row it answered, and a marker
 * where the recording has a gap. Every row, folded or not, stays citable.
 */
export function transcriptItems(meeting: CompanyMeeting, index = speakerIndex(meeting)) {
  const gaps = [...(meeting.captureGaps ?? [])].sort((left, right) => left.atMs - right.atMs);
  const items: TranscriptItem[] = [];
  let running: TranscriptRow | null = null;
  for (const segment of meeting.segments ?? []) {
    const row = { segment, speaker: resolveRowSpeaker(segment, index) };
    if (isBackchannel(row, running)) {
      (running as TranscriptRow).reactions.push(row);
      continue;
    }
    takeGaps(gaps, segment.startMs, items);
    running = { kind: 'row', ...row, reactions: [] };
    items.push(running);
  }
  for (const gap of gaps) items.push({ kind: 'gap', gap });
  return items;
}

/** The voices to show as chips: not merged away, the most talk time first. */
export const panelVoices = (meeting: CompanyMeeting) =>
  (meeting.speakers ?? [])
    .filter((voice) => !voice.mergedInto)
    .sort(
      (left, right) => right.speechMs - left.speechMs || left.displayOrder - right.displayOrder,
    );

/** Voices merged into `label`, so the chip can offer to separate them again. */
export const mergedInto = (meeting: CompanyMeeting, label: string) =>
  (meeting.speakers ?? []).filter((voice) => voice.mergedInto === label);

/** The other voice a person already names, when there is one (the popover offers a merge). */
export function voiceOfPerson(
  meeting: CompanyMeeting,
  participantId: string,
  except: string | null,
) {
  return (
    panelVoices(meeting).find(
      (voice) => voice.participantId === participantId && voice.label !== except,
    ) ?? null
  );
}

/**
 * How many rows "Everyone labelled X" changes: naming a voice moves its rows
 * that have no person of their own (plus the row pointed at); merging moves
 * every row of the voice.
 */
export function labelRowCount(
  meeting: CompanyMeeting,
  label: string,
  target: 'person' | 'voice',
  pointedAt: number | null = null,
) {
  return (meeting.segments ?? []).filter(
    (segment) =>
      segment.speaker === label &&
      (target === 'voice' || !segment.participantId || segment.index === pointedAt),
  ).length;
}

/**
 * Rows a merge of two voices moves. The server keeps a named voice as the
 * root, so merging a named voice into an unnamed one moves the unnamed one's rows.
 */
export function mergeMovedRows(
  meeting: CompanyMeeting,
  from: MeetingSpeaker,
  into: MeetingSpeaker,
) {
  const moving = from.kind !== 'unknown' && into.kind === 'unknown' ? into.label : from.label;
  return labelRowCount(meeting, moving, 'voice');
}

/** The sample rows of a voice as time ranges, in time order. */
export function sampleRanges(meeting: CompanyMeeting, voice: MeetingSpeaker) {
  const rows = new Map((meeting.segments ?? []).map((segment) => [segment.index, segment]));
  return voice.sampleSegments
    .map((index) => rows.get(index))
    .filter(
      (segment): segment is MeetingSegment & { startMs: number; endMs: number } =>
        segment?.startMs != null && segment.endMs != null,
    )
    .map((segment) => ({ index: segment.index, startMs: segment.startMs, endMs: segment.endMs }));
}

const two = (value: number) => String(value).padStart(2, '0');
/** m:ss, or h:mm:ss from an hour. */
export function clockLabel(milliseconds: number | null) {
  if (milliseconds == null) return '—';
  const seconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds / 60) % 60;
  return hours
    ? `${hours}:${two(minutes)}:${two(seconds % 60)}`
    : `${minutes}:${two(seconds % 60)}`;
}

/** Whole minutes of talk, rounded, never 0 for a voice that spoke. */
export const talkMinutes = (speechMs: number) =>
  speechMs > 0 ? Math.max(1, Math.round(speechMs / 60_000)) : 0;
